import type {
  AttachmentDto,
  BoardDto,
  CardDto,
  CommentDto,
  LabelColor,
  Priority,
  RecurrenceInput,
} from '@kanban/shared';
import { LABEL_COLORS, PRIORITY_LABELS, dueState, parseRrule } from '@kanban/shared';
import { Suspense, lazy, useEffect, useState } from 'react';
import {
  useArchiveCard,
  useAssignee,
  useAttachments,
  useBoard,
  useBoards,
  useCardActivity,
  useCardDetail,
  useCardLabels,
  useChecklist,
  useComments,
  useCreateLabel,
  useMoveCard,
  useMoveCardToBoard,
  useNudge,
  usePatchCard,
  useRecurrence,
  useReminderMutations,
  useReminders,
} from '../lib/boards.js';
import { describeActivity } from '../lib/activity.js';
import { positionForDrop } from '../lib/drag-position.js';
import { fromDueInput, toDueInput } from '../lib/due-input.js';
import { offsetLabel, relativeTime } from '../lib/format.js';
import { useCurrentUser } from '../lib/session.js';
import { useSheetDrag } from '../lib/sheet-drag.js';
import { Avatar } from './Avatar.js';

// Erst beim Oeffnen laden (SPEC 9.1 braucht Markdown, das Board nicht).
const MarkdownView = lazy(() => import('./MarkdownView.js'));

/**
 * Kartendetail als Bottom Sheet (SPEC 8.1): faehrt auf Mobil von unten ein,
 * der Board-Kontext bleibt dahinter sichtbar. Ab `md:` rechte Seitenleiste.
 */
export function CardSheet({
  board,
  card,
  onClose,
}: {
  board: BoardDto;
  card: CardDto;
  onClose: () => void;
}) {
  const detail = useCardDetail(card.id);
  const patchCard = usePatchCard(board.id);
  const moveCard = useMoveCard(board.id);
  const moveCardToBoard = useMoveCardToBoard(board.id);
  const archiveCard = useArchiveCard(board.id);
  const assignee = useAssignee(board.id);
  const checklist = useChecklist(board.id, card.id);
  const cardLabels = useCardLabels(board.id, card.id);
  const createLabel = useCreateLabel(board.id);
  const nudge = useNudge(card.id);
  const me = useCurrentUser();
  const boards = useBoards();

  const [title, setTitle] = useState(card.title);
  const [description, setDescription] = useState(card.description ?? '');
  const [showPreview, setShowPreview] = useState(Boolean(card.description));
  const [due, setDue] = useState(() => toDueInput(card.dueAt, card.dueHasTime));
  const [priority, setPriority] = useState<Priority>(card.priority);
  const [isPrivate, setIsPrivate] = useState(card.isPrivate);
  const [newItem, setNewItem] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newLabelColor, setNewLabelColor] = useState<LabelColor>('ocean');
  /*
   * Zielboard fuer das Verschieben ueber Board-Grenzen (SPEC-Ergaenzung).
   * Bleibt es beim aktuellen Board, aendert sich am bestehenden
   * "Spalte"-Feld unten nichts — erst eine andere Wahl hier blendet ein
   * zweites Spalten-Feld fuer das Zielboard ein.
   */
  const [zielBoardId, setZielBoardId] = useState(board.id);
  const zielBoard = useBoard(zielBoardId !== board.id ? zielBoardId : undefined);

  const drag = useSheetDrag(onClose);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /**
   * Was am Formular offen ist, gegenueber dem gespeicherten Stand.
   *
   * Eigene Funktion, weil zwei Knoepfe sie brauchen: „Speichern" und
   * „Wiederholung setzen". Zwei Rechnungen darueber, was sich geaendert hat,
   * waeren zwei, die auseinanderlaufen koennen.
   */
  function pendingPatch(): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    if (title.trim() && title !== card.title) patch.title = title.trim();
    if (description !== (card.description ?? '')) patch.description = description || null;
    const next = fromDueInput(due);
    if (next.dueAt !== card.dueAt || next.dueHasTime !== card.dueHasTime) {
      patch.dueAt = next.dueAt;
      patch.dueHasTime = next.dueHasTime;
    }
    if (priority !== card.priority) patch.priority = priority;
    if (isPrivate !== card.isPrivate) patch.isPrivate = isPrivate;
    return patch;
  }

  /** Offene Aenderungen sichern, ohne das Sheet zu schliessen. */
  async function saveIfDirty(): Promise<void> {
    const patch = pendingPatch();
    if (Object.keys(patch).length === 0) return;
    await patchCard.mutateAsync({ cardId: card.id, patch, updatedAt: card.updatedAt });
  }

  function save() {
    const patch = pendingPatch();
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    patchCard.mutate({ cardId: card.id, patch, updatedAt: card.updatedAt }, { onSuccess: onClose });
  }

  const assignedIds = new Set(card.assignees.map((a) => a.id));

  /*
   * Ueberfaelligkeit aus derselben Funktion, die den Chip faerbt — und
   * derselben, die der Server prueft. Zwei Definitionen von „ueberfaellig"
   * waeren ein Knopf, der da ist und dann abgelehnt wird.
   */
  const overdue = dueState(card.dueAt, card.completedAt) === 'overdue';

  /*
   * Es muss jemanden geben, den die Karte etwas angeht: entweder
   * Verantwortliche ausser mir, oder gar keine — dann sind alle gemeint.
   * Bin ich der einzige Verantwortliche, mahne ich mich selbst an.
   */
  const canNudge =
    card.assignees.length === 0 || card.assignees.some((person) => person.id !== me.id);
  const labelIds = new Set(card.labels.map((l) => l.id));
  const items = detail.data?.checklist ?? [];

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-[rgb(0_0_0/45%)] md:items-stretch md:justify-end"
      role="dialog"
      aria-modal="true"
      aria-label={`Karte ${card.title}`}
      data-testid="card-sheet"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={drag.sheetRef}
        className="max-h-[92vh] w-full overflow-y-auto rounded-t-[var(--radius-lg)] bg-surface shadow-[var(--shadow-3)] md:max-h-none md:w-[26rem] md:rounded-none md:rounded-l-[var(--radius-lg)]"
      >
        {/*
          Ziehgriff — auf Mobil das uebliche Zeichen fuer "das geht zu", und
          seit dieser Aenderung tut er es auch: nach unten ziehen schliesst
          die Karte und VERWIRFT die Eingaben, genau wie „Schliessen" und
          Escape. Gespeichert wird nur ueber „Speichern".
        */}
        <div
          {...drag.handleProps}
          data-testid="sheet-handle"
          className="sticky top-0 z-10 flex cursor-grab justify-center bg-surface pt-2 pb-2 md:hidden"
        >
          <span className="h-1 w-10 rounded-full bg-border-strong" aria-hidden="true" />
        </div>

        <div className="p-4">
          <label className="block text-xs font-medium text-muted" htmlFor="card-title">
            Titel
          </label>
          <input
            id="card-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-base font-medium"
          />

          <div className="mt-4 flex items-center justify-between">
            <label className="block text-xs font-medium text-muted" htmlFor="card-description">
              Beschreibung
            </label>
            <button
              type="button"
              data-testid="toggle-preview"
              onClick={() => setShowPreview((current) => !current)}
              className="rounded-[var(--radius-sm)] px-2 py-1 text-xs text-accent"
            >
              {showPreview ? 'Bearbeiten' : 'Vorschau'}
            </button>
          </div>

          {showPreview ? (
            <div
              data-testid="description-preview"
              className="mt-1 min-h-24 rounded-[var(--radius)] border border-border px-3 py-2"
              onDoubleClick={() => setShowPreview(false)}
            >
              {description.trim() ? (
                <Suspense fallback={<p className="text-sm text-muted">Lädt …</p>}>
                  <MarkdownView>{description}</MarkdownView>
                </Suspense>
              ) : (
                <p className="text-sm text-muted">Keine Beschreibung</p>
              )}
            </div>
          ) : (
            <textarea
              id="card-description"
              rows={5}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Markdown: **fett**, - Liste, [Link](https://…)"
              className="mt-1 w-full resize-y rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-sm"
            />
          )}

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-muted" htmlFor="card-due">
                Fällig
              </label>
              <input
                id="card-due"
                type="date"
                value={due.date}
                onChange={(event) => setDue((current) => ({ ...current, date: event.target.value }))}
                className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-sm"
                style={{ minHeight: 'var(--touch-target)' }}
              />
              {/*
                Uhrzeit ist optional (SPEC 9.1) und erscheint erst mit einem
                Datum — ein Zeitfeld ohne Tag hat keine Bedeutung und wuerde
                nur die Frage aufwerfen, welcher Tag gemeint ist.
              */}
              {due.date && (
                <>
                  <label className="mt-2 block text-xs font-medium text-muted" htmlFor="card-due-time">
                    Uhrzeit (optional)
                  </label>
                  <input
                    id="card-due-time"
                    type="time"
                    /*
                     * Viertelstunden-Raster: das Zeit-Rad auf iOS rollt damit
                     * in vier Stufen statt sechzig. `step` rastet nur die
                     * Bedienung — gerundet wird zusaetzlich in `due-input.ts`,
                     * damit im Feld nicht etwas anderes steht als in der
                     * Datenbank.
                     */
                    step={900}
                    data-testid="card-due-time"
                    value={due.time}
                    onChange={(event) =>
                      setDue((current) => ({ ...current, time: event.target.value }))
                    }
                    className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-sm"
                    style={{ minHeight: 'var(--touch-target)' }}
                  />
                  <p className="mt-1 text-xs text-muted">
                    {due.time ? 'Meldet sich zu dieser Uhrzeit.' : 'Ohne Uhrzeit: bis Tagesende.'}
                  </p>
                </>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-muted" htmlFor="card-priority">
                Priorität
              </label>
              <select
                id="card-priority"
                value={priority}
                onChange={(event) => setPriority(Number(event.target.value) as Priority)}
                className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-sm"
                style={{ minHeight: 'var(--touch-target)' }}
              >
                {([0, 1, 2, 3] as Priority[]).map((value) => (
                  <option key={value} value={value}>
                    {PRIORITY_LABELS[value]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/*
            Nur sichtbar fuer die anlegende Person — alle anderen koennten
            die Karte ohnehin nicht mehr oeffnen, sobald sie privat ist.
          */}
          {card.createdById === me.id && (
            <div className="mt-4">
              <button
                type="button"
                data-testid="toggle-private"
                aria-pressed={isPrivate}
                onClick={() => setIsPrivate((current) => !current)}
                className={`flex w-full items-center justify-between rounded-[var(--radius)] border px-3 text-left text-sm ${
                  isPrivate ? 'border-accent bg-accent-soft' : 'border-border'
                }`}
                style={{ minHeight: 'var(--touch-target)' }}
              >
                <span>Privat</span>
                <span className="text-xs text-muted">
                  {isPrivate ? 'nur für mich sichtbar' : 'für das Board sichtbar'}
                </span>
              </button>
            </div>
          )}

          {/*
            Verantwortlich als Reihe von Avataren.
            Der ausgeschriebene Name je Person kostete bei vier
            Familienmitgliedern einen halben Bildschirm. Er verschwindet aus
            der ANSICHT, nicht aus der Bedienung: `aria-label` traegt ihn
            weiter, und unter der Reihe steht, wer ausgewaehlt ist — sonst
            muesste man die Avatare auswendig kennen.
          */}
          <fieldset className="mt-4">
            <legend className="text-xs font-medium text-muted">Verantwortlich</legend>
            <div className="mt-2 flex flex-wrap gap-1">
              {board.members.map((member) => {
                const isAssigned = assignedIds.has(member.id);
                return (
                  <button
                    key={member.id}
                    type="button"
                    aria-pressed={isAssigned}
                    aria-label={member.name}
                    title={member.name}
                    data-testid="assignee-toggle"
                    onClick={() =>
                      assignee.mutate({ cardId: card.id, userId: member.id, assign: !isAssigned })
                    }
                    /*
                     * Trefferflaeche bleibt bei `--touch-target` — der Avatar
                     * wird nicht kleiner, es faellt nur der Text weg. Und
                     * ausgewaehlt ist an Rahmen UND Flaeche erkennbar, nicht
                     * an Farbe allein.
                     */
                    className={`flex items-center justify-center rounded-full border px-1 ${
                      isAssigned ? 'border-accent bg-accent-soft' : 'border-transparent opacity-60'
                    }`}
                    style={{ minHeight: 'var(--touch-target)', minWidth: 'var(--touch-target)' }}
                  >
                    <Avatar name={member.name} color={member.avatarColor} />
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-muted" data-testid="assignee-names">
              {card.assignees.length > 0
                ? card.assignees.map((person) => person.name).join(', ')
                : 'Niemand — die Aufgabe gilt für alle'}
            </p>
          </fieldset>

          <fieldset className="mt-4">
            <legend className="text-xs font-medium text-muted">Labels</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {board.labels.map((label) => {
                const active = labelIds.has(label.id);
                return (
                  <button
                    key={label.id}
                    type="button"
                    aria-pressed={active}
                    data-testid="label-chip"
                    onClick={() => cardLabels.mutate({ labelId: label.id, assign: !active })}
                    className={`flex items-center gap-2 rounded-full border px-3 text-sm ${
                      active ? 'border-accent bg-accent-soft' : 'border-border bg-surface'
                    }`}
                    style={{ minHeight: 'var(--touch-target)' }}
                  >
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: `var(--color-label-${label.color})` }}
                      aria-hidden="true"
                    />
                    {label.name}
                  </button>
                );
              })}
            </div>

            <div className="mt-2 flex gap-2">
              <input
                value={newLabel}
                onChange={(event) => setNewLabel(event.target.value)}
                placeholder="Neues Label"
                aria-label="Neues Label"
                className="min-w-0 flex-1 rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-sm"
              />
              <select
                value={newLabelColor}
                onChange={(event) => setNewLabelColor(event.target.value as LabelColor)}
                aria-label="Farbe des neuen Labels"
                className="rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
              >
                {LABEL_COLORS.map((color) => (
                  <option key={color} value={color}>
                    {color}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  if (!newLabel.trim()) return;
                  createLabel.mutate(
                    { name: newLabel.trim(), color: newLabelColor },
                    { onSuccess: () => setNewLabel('') },
                  );
                }}
                className="rounded-[var(--radius)] border border-border px-3 text-sm"
                style={{ minHeight: 'var(--touch-target)' }}
              >
                +
              </button>
            </div>
          </fieldset>

          <fieldset className="mt-4">
            <legend className="text-xs font-medium text-muted">
              Checkliste {items.length > 0 && `(${items.filter((i) => i.isDone).length}/${items.length})`}
            </legend>

            <ul className="mt-2 space-y-1" data-testid="checklist">
              {items.map((item) => (
                <li key={item.id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={item.isDone}
                    onChange={() => checklist.toggle.mutate({ id: item.id, isDone: !item.isDone })}
                    id={`item-${item.id}`}
                    className="h-5 w-5 shrink-0 accent-[var(--color-accent)]"
                  />
                  <label
                    htmlFor={`item-${item.id}`}
                    className={`flex-1 text-sm ${item.isDone ? 'text-muted line-through' : ''}`}
                  >
                    {item.text}
                  </label>
                  <button
                    type="button"
                    aria-label={`${item.text} entfernen`}
                    onClick={() => checklist.remove.mutate(item.id)}
                    className="px-2 text-muted"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>

            <form
              className="mt-2 flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                if (!newItem.trim()) return;
                checklist.add.mutate(newItem.trim(), { onSuccess: () => setNewItem('') });
              }}
            >
              <input
                value={newItem}
                onChange={(event) => setNewItem(event.target.value)}
                placeholder="Punkt hinzufügen"
                aria-label="Neuer Checklistenpunkt"
                className="min-w-0 flex-1 rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-sm"
              />
              <button
                type="submit"
                className="rounded-[var(--radius)] border border-border px-3 text-sm"
                style={{ minHeight: 'var(--touch-target)' }}
              >
                +
              </button>
            </form>
          </fieldset>

          <div className="mt-4">
            <label className="block text-xs font-medium text-muted" htmlFor="card-board">
              Board
            </label>
            <select
              id="card-board"
              data-testid="card-board"
              value={zielBoardId}
              onChange={(event) => setZielBoardId(event.target.value)}
              className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-sm"
              style={{ minHeight: 'var(--touch-target)' }}
            >
              <option value={board.id}>{board.title}</option>
              {(boards.data ?? [])
                .filter((other) => other.id !== board.id)
                .map((other) => (
                  <option key={other.id} value={other.id}>
                    {other.title}
                  </option>
                ))}
            </select>
          </div>

          {zielBoardId === board.id ? (
            <div className="mt-4">
              <label className="block text-xs font-medium text-muted" htmlFor="card-column">
                Spalte
              </label>
              <select
                id="card-column"
                value={card.columnId}
                onChange={(event) => {
                  const columnId = event.target.value;
                  const others =
                    board.columns.find((c) => c.id === columnId)?.cards.filter((c) => c.id !== card.id) ??
                    [];
                  const target = { columnId, index: others.length };
                  moveCard.mutate({
                    cardId: card.id,
                    columnId,
                    target,
                    position: positionForDrop(board.columns, card.id, target),
                  });
                }}
                className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-sm"
                style={{ minHeight: 'var(--touch-target)' }}
              >
                {board.columns.map((column) => (
                  <option key={column.id} value={column.id}>
                    {column.title}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="mt-4">
              <label className="block text-xs font-medium text-muted" htmlFor="card-target-column">
                Spalte im Zielboard
              </label>
              <select
                id="card-target-column"
                data-testid="card-target-column"
                disabled={!zielBoard.data}
                defaultValue=""
                onChange={(event) => {
                  const columnId = event.target.value;
                  if (!columnId) return;
                  moveCardToBoard.mutate(
                    { cardId: card.id, targetBoardId: zielBoardId, columnId },
                    { onSuccess: onClose },
                  );
                }}
                className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-sm"
                style={{ minHeight: 'var(--touch-target)' }}
              >
                <option value="" disabled>
                  {zielBoard.isLoading ? 'Lädt …' : 'Spalte wählen'}
                </option>
                {(zielBoard.data?.columns ?? []).map((column) => (
                  <option key={column.id} value={column.id}>
                    {column.title}
                  </option>
                ))}
              </select>
              {/*
                Verantwortliche und Labels, die nicht zum Zielboard passen,
                gehen beim Verschieben verloren — hier gesagt, bevor es
                passiert, nicht erst danach im Verlauf nachgelesen.
              */}
              <p className="mt-1 text-xs text-muted">
                Verantwortliche und Labels, die im Zielboard nicht gelten, werden dabei entfernt.
              </p>
            </div>
          )}

          {/*
            `due.date` statt `card.dueAt`: der Formularstand, nicht der
            gespeicherte. Sonst bleiben beide Abschnitte gesperrt, bis das
            Datum durch den Server gelaufen ist — und weil „Speichern" das
            Sheet schliesst, hiesse das: Karte ein zweites Mal oeffnen.
          */}
          <ReminderSection cardId={card.id} hasDueAt={due.date !== ''} />

          <RecurrenceSection
            board={board}
            cardId={card.id}
            hasDueAt={due.date !== ''}
            onBeforeSave={saveIfDirty}
          />

          <AttachmentSection
            boardId={board.id}
            cardId={card.id}
            attachments={detail.data?.attachments ?? []}
          />

          <CommentSection
            boardId={board.id}
            cardId={card.id}
            comments={detail.data?.comments ?? []}
          />

          <ActivitySection cardId={card.id} />

          <div className="mt-6 flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={patchCard.isPending}
              className="flex-1 rounded-[var(--radius)] bg-accent px-4 text-sm font-medium text-[var(--color-accent-contrast)] disabled:opacity-60"
              style={{ minHeight: 'var(--touch-target)' }}
            >
              Speichern
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-[var(--radius)] border border-border bg-surface px-4 text-sm"
              style={{ minHeight: 'var(--touch-target)' }}
            >
              Schließen
            </button>
          </div>

          {/*
            Anmahnen gibt es nur, wo es Sinn hat: bei einer ueberfaelligen
            Karte, die jemand anderes betrifft. Als eigener Knopf und nicht
            auf der Karte im Board — `CardTile` ist bewusst kein Button und
            darf keinen enthalten, sonst verweigert dnd-kit den Drag-Start.
          */}
          {overdue && canNudge && (
            <button
              type="button"
              data-testid="nudge"
              onClick={() => nudge.mutate()}
              disabled={nudge.isPending}
              className="mt-3 w-full rounded-[var(--radius)] border border-caution px-4 text-sm text-caution disabled:opacity-60"
              style={{ minHeight: 'var(--touch-target)' }}
            >
              Erinnern
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              archiveCard.mutate(card.id);
              onClose();
            }}
            className="mt-3 w-full rounded-[var(--radius)] border border-danger px-4 text-sm text-danger"
            style={{ minHeight: 'var(--touch-target)' }}
          >
            Archivieren
          </button>
        </div>
      </div>
    </div>
  );
}

/** Die Wiederholungen, die im Haushalt tatsaechlich vorkommen. */
const PATTERNS: { id: string; label: string; input: Omit<RecurrenceInput, 'rotation'> }[] = [
  { id: 'taeglich', label: 'täglich', input: { freq: 'DAILY', interval: 1 } },
  { id: 'woechentlich', label: 'wöchentlich', input: { freq: 'WEEKLY', interval: 1 } },
  {
    id: 'zweiwoechentlich',
    label: 'alle zwei Wochen',
    input: { freq: 'WEEKLY', interval: 2 },
  },
  { id: 'monatlich', label: 'monatlich', input: { freq: 'MONTHLY', interval: 1 } },
  {
    id: 'monatsende',
    label: 'am Monatsende',
    input: { freq: 'MONTHLY', interval: 1, byMonthDay: -1 },
  },
];

/**
 * Wiederkehrende Aufgabe mit Rotation (SPEC 9.4).
 *
 * Der Wochentag kommt aus der Faelligkeit der Karte, nicht aus einer eigenen
 * Auswahl: „woechentlich" heisst „wieder an dem Tag, an dem es jetzt faellig
 * ist". Zwei Stellen, an denen derselbe Tag steht, waeren zwei Stellen, die
 * auseinanderlaufen koennen.
 */
function RecurrenceSection({
  board,
  cardId,
  hasDueAt,
  onBeforeSave,
}: {
  board: BoardDto;
  cardId: string;
  hasDueAt: boolean;
  /** Offene Formularaenderungen sichern, bevor die Regel gesetzt wird. */
  onBeforeSave: () => Promise<void>;
}) {
  const detail = useCardDetail(cardId);
  const { save, remove } = useRecurrence(board.id, cardId);
  const current = detail.data?.recurrence ?? null;

  const [pattern, setPattern] = useState('woechentlich');
  const [rotation, setRotation] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  const active = PATTERNS.find((entry) => entry.id === pattern) ?? PATTERNS[1]!;

  /*
   * Erst die Karte, dann die Regel.
   *
   * Der Server rechnet den naechsten Termin aus `card.dueAt`. Stuende dort
   * noch nichts, weil das Datum nur im Formular steht, entstuende eine
   * Wiederholung ohne Rhythmus — im UI sichtbar als „Jede Woche" fuer eine
   * Karte ohne Datum.
   */
  async function setRecurrence() {
    setSaving(true);
    try {
      await onBeforeSave();
      save.mutate({ ...active.input, ...(rotation.length > 0 ? { rotation } : {}) });
    } finally {
      setSaving(false);
    }
  }

  if (current) {
    const next = current.nextUserId
      ? board.members.find((member) => member.id === current.nextUserId)
      : null;

    return (
      <section className="mt-5" aria-labelledby="wiederholung">
        <h3 id="wiederholung" className="text-xs font-medium text-muted">
          Wiederholung
        </h3>
        <div
          data-testid="recurrence-active"
          className="mt-2 rounded-[var(--radius)] bg-surface-muted px-3 py-2 text-sm"
        >
          <p>{describeRrule(current.rrule)}</p>
          {next && (
            <p className="mt-0.5 text-xs text-muted">Als Nächstes ist {next.name} dran.</p>
          )}
          <button
            type="button"
            data-testid="remove-recurrence"
            onClick={() => remove.mutate()}
            className="mt-2 text-xs text-danger underline"
          >
            Wiederholung entfernen
          </button>
        </div>
      </section>
    );
  }

  /*
   * Zugeklappt, solange keine Wiederholung eingestellt ist. Der Zweig
   * darueber — eine Karte, die sich schon wiederholt — bleibt immer offen:
   * das ist die Sorte Einstellung, die man nicht suchen soll.
   */
  if (!open) {
    return (
      <section className="mt-5" aria-labelledby="wiederholung">
        <button
          type="button"
          id="wiederholung"
          data-testid="toggle-recurrence"
          aria-expanded={false}
          onClick={() => setOpen(true)}
          className="flex w-full items-center justify-between text-xs font-medium text-muted"
          style={{ minHeight: 'var(--touch-target)' }}
        >
          <span>Wiederholung</span>
          <span aria-hidden="true">▸</span>
        </button>
      </section>
    );
  }

  return (
    <section className="mt-5" aria-labelledby="wiederholung">
      <button
        type="button"
        id="wiederholung"
        data-testid="toggle-recurrence"
        aria-expanded
        onClick={() => setOpen(false)}
        className="flex w-full items-center justify-between text-xs font-medium text-muted"
        style={{ minHeight: 'var(--touch-target)' }}
      >
        <span>Wiederholung</span>
        <span aria-hidden="true">▾</span>
      </button>

      {!hasDueAt && (
        <p className="mt-1 text-xs text-muted">
          Ohne Fälligkeitsdatum gibt es keinen Rhythmus. Setz oben ein Datum.
        </p>
      )}

      <div className="mt-2 flex gap-2">
        <label className="sr-only" htmlFor="recurrence-pattern">
          Rhythmus
        </label>
        <select
          id="recurrence-pattern"
          value={pattern}
          onChange={(event) => setPattern(event.target.value)}
          className="min-w-0 flex-1 rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
          style={{ minHeight: 'var(--touch-target)' }}
        >
          {PATTERNS.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          data-testid="add-recurrence"
          disabled={!hasDueAt || saving || save.isPending}
          onClick={() => void setRecurrence()}
          className="shrink-0 rounded-[var(--radius)] border border-border px-3 text-sm disabled:opacity-60"
          style={{ minHeight: 'var(--touch-target)' }}
        >
          Setzen
        </button>
      </div>

      <p className="mt-2 text-xs text-muted">
        Reihum (in dieser Reihenfolge antippen) — sonst bleiben die Verantwortlichen wie sie sind:
      </p>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {board.members.map((member) => {
          const index = rotation.indexOf(member.id);
          return (
            <button
              key={member.id}
              type="button"
              data-testid="rotation-member"
              aria-pressed={index >= 0}
              onClick={() =>
                setRotation((current) =>
                  current.includes(member.id)
                    ? current.filter((id) => id !== member.id)
                    : [...current, member.id],
                )
              }
              className={`rounded-full border px-2 py-0.5 text-xs ${
                index >= 0 ? 'border-accent bg-accent-soft text-accent' : 'border-border text-muted'
              }`}
            >
              {index >= 0 ? `${index + 1}. ` : ''}
              {member.name}
            </button>
          );
        })}
      </div>
    </section>
  );
}

/** RRULE in einen Satz, den man ohne RFC im Kopf lesen kann. */
function describeRrule(value: string): string {
  const rule = parseRrule(value);
  if (!rule) return value;

  const every =
    rule.interval === 1 ? '' : rule.interval === 2 ? 'jede zweite ' : `alle ${rule.interval} `;

  if (rule.freq === 'DAILY') {
    return rule.interval === 1 ? 'Jeden Tag' : `Alle ${rule.interval} Tage`;
  }
  if (rule.freq === 'WEEKLY') {
    return rule.interval === 1 ? 'Jede Woche' : `${every}Woche`.replace(/^./, (c) => c.toUpperCase());
  }
  if (rule.byMonthDay === -1) return 'Am Ende jedes Monats';
  return rule.interval === 1 ? 'Jeden Monat' : `Alle ${rule.interval} Monate`;
}

/**
 * Vorlauf-Angebote in 15-Minuten-Schritten.
 *
 * Bis zwei Stunden fein, danach gröber — jeder Wert bleibt ein Vielfaches von
 * 15. Feiner als eine Viertelstunde bringt im Haushalt nichts, und eine Liste
 * mit sechzig Einträgen wäre auf dem Telefon nicht mehr zu bedienen.
 */
const OFFSET_MINUTES = [0, 15, 30, 45, 60, 75, 90, 105, 120, 180, 360, 720, 1440, 2880];

/**
 * Vorgabe: zum Termin.
 *
 * Vorher stand hier 60 — wer nur schnell eine Erinnerung setzen wollte, bekam
 * ungefragt eine Stunde Vorlauf.
 */
const DEFAULT_OFFSET = 0;

/**
 * Eigene Erinnerungen an einer Karte (SPEC 10.1).
 *
 * Die Faellig-Meldung selbst steht hier nicht: sie entsteht automatisch mit
 * dem Datum und wird im Profil ein- oder ausgeschaltet, nicht je Karte. Hier
 * geht es nur um den zusaetzlichen Vorlauf.
 */
function ReminderSection({ cardId, hasDueAt }: { cardId: string; hasDueAt: boolean }) {
  const reminders = useReminders(cardId);
  const { add, remove } = useReminderMutations(cardId);
  const [offset, setOffset] = useState(DEFAULT_OFFSET);

  const gesetzt = reminders.data ?? [];
  /*
   * Zugeklappt, solange nichts eingestellt ist (Platz auf dem Telefon). Wer
   * schon Erinnerungen hat, soll sie sehen, ohne danach zu suchen — ein
   * Abschnitt, der seinen Inhalt versteckt, ist eine Falle.
   */
  const [open, setOpen] = useState(false);
  const offen = open || gesetzt.length > 0;

  return (
    <section className="mt-5" aria-labelledby="erinnerungen">
      <button
        type="button"
        id="erinnerungen"
        data-testid="toggle-reminders"
        aria-expanded={offen}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between text-xs font-medium text-muted"
        style={{ minHeight: 'var(--touch-target)' }}
      >
        <span>
          Erinnerungen
          {gesetzt.length > 0 && ` (${gesetzt.length})`}
        </span>
        <span aria-hidden="true">{offen ? '▾' : '▸'}</span>
      </button>

      {!offen ? null : (
        <>
      {!hasDueAt && (
        <p className="mt-1 text-xs text-muted">
          Ohne Fälligkeitsdatum meldet sich nichts. Setz oben ein Datum, dann greifen die
          Erinnerungen.
        </p>
      )}

      <ul className="mt-2 space-y-1">
        {gesetzt.map((reminder) => (
          <li
            key={reminder.id}
            data-testid="reminder"
            className="flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-surface-muted px-2 py-1 text-sm"
          >
            <span>
              {offsetLabel(reminder.offsetMinutes)}
              {reminder.sentAt && <span className="text-xs text-muted"> · verschickt</span>}
            </span>
            <button
              type="button"
              aria-label="Erinnerung entfernen"
              onClick={() => remove.mutate(reminder.id)}
              className="shrink-0 text-muted"
              style={{ minWidth: '2rem', minHeight: '2rem' }}
            >
              ✕
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-2 flex gap-2">
        <label className="sr-only" htmlFor="reminder-offset">
          Vorlauf
        </label>
        <select
          id="reminder-offset"
          value={offset}
          onChange={(event) => setOffset(Number(event.target.value))}
          className="min-w-0 flex-1 rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
          style={{ minHeight: 'var(--touch-target)' }}
        >
          {OFFSET_MINUTES.map((minutes) => (
            <option key={minutes} value={minutes}>
              {offsetLabel(minutes)}
            </option>
          ))}
        </select>
        <button
          type="button"
          data-testid="add-reminder"
          onClick={() => add.mutate(offset)}
          disabled={add.isPending}
          className="shrink-0 rounded-[var(--radius)] border border-border px-3 text-sm disabled:opacity-60"
          style={{ minHeight: 'var(--touch-target)' }}
        >
          Hinzufügen
        </button>
      </div>
        </>
      )}
    </section>
  );
}

/** Dateigroesse in einer Zeile, die auf ein Telefon passt. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Anhaenge (SPEC 9.8).
 *
 * Die Vorschau haengt als gewoehnliches `<img>` am authentifizierten
 * Endpunkt: eine Anfrage aus derselben Herkunft traegt das Sitzungscookie von
 * selbst. Der Umweg ueber `fetch` und eine Blob-URL waere mehr Code und ein
 * Speicherleck mehr, das man vergessen kann freizugeben.
 *
 * Heruntergeladen wird ueber einen echten Link statt ueber JavaScript — nur
 * so bietet iOS „In Dateien sichern" an.
 */
function AttachmentSection({
  boardId,
  cardId,
  attachments,
}: {
  boardId: string;
  cardId: string;
  attachments: AttachmentDto[];
}) {
  const me = useCurrentUser();
  const { add, remove } = useAttachments(boardId, cardId);

  return (
    <section className="mt-6" aria-labelledby="anhaenge">
      <h3 id="anhaenge" className="text-xs font-medium text-muted">
        Anhänge {attachments.length > 0 && `(${attachments.length})`}
      </h3>

      <ul className="mt-2 space-y-2" data-testid="attachments">
        {attachments.map((attachment) => (
          <li
            key={attachment.id}
            data-testid="attachment"
            className="flex items-center gap-3 rounded-[var(--radius)] border border-border p-2"
          >
            {attachment.hasThumbnail ? (
              <img
                src={`/api/v1/attachments/${attachment.id}/thumbnail`}
                alt=""
                loading="lazy"
                className="h-12 w-12 shrink-0 rounded-[var(--radius-sm)] object-cover"
              />
            ) : (
              <span
                aria-hidden="true"
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-surface-muted text-lg text-muted"
              >
                🗎
              </span>
            )}

            <div className="min-w-0 flex-1">
              <a
                href={`/api/v1/attachments/${attachment.id}`}
                /*
                 * Kein `download`-Attribut: der Server schickt bereits
                 * `Content-Disposition: attachment` samt Namen (SPEC 11).
                 */
                className="block truncate text-sm text-accent"
              >
                {attachment.filename}
              </a>
              <p className="text-xs text-muted">
                {formatSize(attachment.size)} · {attachment.uploadedBy.name} ·{' '}
                {relativeTime(attachment.createdAt)}
              </p>
            </div>

            {/*
              Loeschen darf, wer hochgeladen hat. Der Server erlaubt es
              zusaetzlich dem Board-Eigentuemer; hier steht der Knopf nur beim
              eigenen Anhang, damit niemand einen sieht, der ihm im Regelfall
              nichts bringt.
            */}
            {attachment.uploadedBy.id === me.id && (
              <button
                type="button"
                data-testid="delete-attachment"
                onClick={() => remove.mutate(attachment.id)}
                className="shrink-0 text-xs text-muted"
              >
                Löschen
              </button>
            )}
          </li>
        ))}
      </ul>

      <label
        className="mt-2 flex cursor-pointer items-center justify-center rounded-[var(--radius)] border border-dashed border-border text-sm text-muted"
        style={{ minHeight: 'var(--touch-target)' }}
      >
        {add.isPending ? 'Wird hochgeladen …' : 'Datei anhängen'}
        <input
          type="file"
          /*
           * Ohne `capture`: damit laesst iOS die Wahl zwischen Kamera und
           * Mediathek. Mit `capture` gaebe es nur noch die Kamera.
           */
          accept="image/*,application/pdf"
          data-testid="attachment-input"
          className="sr-only"
          disabled={add.isPending}
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Feld zuruecksetzen: sonst loest dieselbe Datei beim zweiten Mal
            // kein `change` aus.
            event.target.value = '';
            if (file) add.mutate(file);
          }}
        />
      </label>
    </section>
  );
}

/**
 * Kommentare (SPEC 9.1).
 *
 * Reiner Text, kein Markdown: die Beschreibung ist der Ort fuer Struktur,
 * ein Kommentar ist ein Zuruf. Das spart zugleich die Vorschau und die Frage,
 * was passiert, wenn jemand HTML hineinschreibt.
 */
function CommentSection({
  boardId,
  cardId,
  comments,
}: {
  boardId: string;
  cardId: string;
  comments: CommentDto[];
}) {
  const me = useCurrentUser();
  const { create, patch, remove } = useComments(boardId, cardId);
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);

  return (
    <section className="mt-6" aria-labelledby="kommentare">
      <h3 id="kommentare" className="text-xs font-medium text-muted">
        Kommentare {comments.length > 0 && `(${comments.length})`}
      </h3>

      <ul className="mt-2 space-y-3" data-testid="comments">
        {comments.map((comment) => (
          <li key={comment.id} className="flex gap-2" data-testid="comment">
            <Avatar name={comment.author.name} color={comment.author.avatarColor} />
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted">
                <span className="font-medium text-[color:var(--color-text)]">
                  {comment.author.name}
                </span>{' '}
                · {relativeTime(comment.createdAt)}
                {comment.editedAt && ' · bearbeitet'}
              </p>

              {editing?.id === comment.id ? (
                <form
                  className="mt-1"
                  onSubmit={(event) => {
                    event.preventDefault();
                    const body = editing.body.trim();
                    if (!body) return;
                    patch.mutate(
                      { commentId: comment.id, body },
                      { onSuccess: () => setEditing(null) },
                    );
                  }}
                >
                  <textarea
                    value={editing.body}
                    onChange={(event) => setEditing({ id: comment.id, body: event.target.value })}
                    rows={3}
                    aria-label="Kommentar bearbeiten"
                    className="w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-sm"
                  />
                  <div className="mt-1 flex gap-2">
                    <button type="submit" className="text-xs text-accent">
                      Übernehmen
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(null)}
                      className="text-xs text-muted"
                    >
                      Abbrechen
                    </button>
                  </div>
                </form>
              ) : (
                <p className="whitespace-pre-wrap break-words text-sm">{comment.body}</p>
              )}

              {/*
                Aendern und Loeschen nur am eigenen Beitrag. Der Server
                besteht ohnehin darauf (A10-Denkweise) — hier geht es darum,
                dass niemand einen Knopf sieht, der ihm nichts bringt.
              */}
              {comment.author.id === me.id && editing?.id !== comment.id && (
                <div className="mt-1 flex gap-3">
                  <button
                    type="button"
                    data-testid="edit-comment"
                    onClick={() => setEditing({ id: comment.id, body: comment.body })}
                    className="text-xs text-muted"
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    data-testid="delete-comment"
                    onClick={() => remove.mutate(comment.id)}
                    className="text-xs text-muted"
                  >
                    Löschen
                  </button>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>

      <form
        className="mt-3"
        onSubmit={(event) => {
          event.preventDefault();
          const body = draft.trim();
          if (!body) return;
          create.mutate(body, { onSuccess: () => setDraft('') });
        }}
      >
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          rows={2}
          placeholder="Kommentar schreiben"
          aria-label="Neuer Kommentar"
          data-testid="comment-input"
          className="w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-sm"
        />
        <button
          type="submit"
          data-testid="comment-submit"
          disabled={draft.trim().length === 0 || create.isPending}
          className="mt-2 w-full rounded-[var(--radius)] border border-border px-4 text-sm disabled:opacity-50"
          style={{ minHeight: 'var(--touch-target)' }}
        >
          Senden
        </button>
      </form>
    </section>
  );
}

/**
 * Aktivitaetsverlauf (SPEC 9.6).
 *
 * Zugeklappt und erst auf Verlangen geladen: die Karte soll auf dem Telefon
 * sofort dastehen, und der Verlauf ist das, was man zuletzt braucht — aber
 * eben manchmal dringend („wer hat das verschoben?").
 */
function ActivitySection({ cardId }: { cardId: string }) {
  const [open, setOpen] = useState(false);
  const activity = useCardActivity(cardId, open);

  const entries = (activity.data ?? [])
    .map((entry) => ({ entry, text: describeActivity(entry) }))
    .filter((row): row is { entry: (typeof row)['entry']; text: string } => row.text !== null);

  return (
    <section className="mt-6" aria-labelledby="verlauf">
      <button
        type="button"
        id="verlauf"
        data-testid="toggle-activity"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="text-xs font-medium text-muted"
      >
        Verlauf {open ? '▾' : '▸'}
      </button>

      {open && (
        <ul className="mt-2 space-y-2" data-testid="activity">
          {activity.isPending && <li className="text-sm text-muted">Wird geladen …</li>}
          {!activity.isPending && entries.length === 0 && (
            <li className="text-sm text-muted">Noch nichts passiert.</li>
          )}
          {entries.map(({ entry, text }) => (
            <li key={entry.id} className="text-sm" data-testid="activity-entry">
              <span className="font-medium">{entry.actor.name}</span> {text}
              <span className="text-muted"> · {relativeTime(entry.createdAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
